import React, { useState } from 'react';
import { Plus, Calendar, Hash, Instagram, Youtube, Linkedin, Twitter, BookText as TikTok, CheckCircle, Clock, Grid, List } from 'lucide-react';
import type { Post } from '../../types/Post';
import PostModal from './PostModal';
import NewPostModal from './NewPostModal';
import CalendarView from '../calendar/CalendarView';

type PostListProps = {
  posts: Post[];
  onAddPost: (post: Omit<Post, 'id' | 'profile_id' | 'created_at' | 'updated_at'>) => void;
  onDeletePost: (id: string) => void;
  onUpdatePost: (id: string, updates: Partial<Post>) => void;
};

export default function PostList({ posts, onAddPost, onDeletePost, onUpdatePost }: PostListProps) {
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [showNewPostModal, setShowNewPostModal] = useState(false);

  const getChannelIcon = (channel: string) => {
    const icons = {
      instagram: <Instagram className="w-4 h-4" />,
      youtube: <Youtube className="w-4 h-4" />,
      linkedin: <Linkedin className="w-4 h-4" />,
      twitter: <Twitter className="w-4 h-4" />,
      tiktok: <TikTok className="w-4 h-4" />
    };
    return icons[channel as keyof typeof icons];
  };

  const getChannelColor = (channel: string) => {
    const colors = {
      instagram: 'bg-pink-100 text-pink-800',
      youtube: 'bg-red-100 text-red-800',
      linkedin: 'bg-blue-100 text-blue-800',
      twitter: 'bg-sky-100 text-sky-800',
      tiktok: 'bg-purple-100 text-purple-800'
    };
    return colors[channel as keyof typeof colors];
  };

  const getStatusColor = (status: string) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      scheduled: 'bg-blue-100 text-blue-800',
      published: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800'
    };
    return colors[status as keyof typeof colors];
  };

  const filteredPosts = posts.filter(post => 
    showCompleted ? post.status === 'completed' : post.status !== 'completed'
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Posts</h1>
          <p className="text-gray-500 mt-1">Gerencie seus posts em um só lugar</p>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <button
            onClick={() => setShowCompleted(!showCompleted)}
            className={`text-sm px-3 py-1.5 rounded-lg transition-colors ${
              showCompleted 
                ? 'text-green-700 hover:text-green-800' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            {showCompleted ? 'Voltar aos posts' : 'Ver posts concluídos'}
          </button>
          {!showCompleted && (
            <>
              <div className="flex items-center gap-2 border rounded-lg p-1">
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'list' ? 'bg-gray-100' : 'hover:bg-gray-50'
                  }`}
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('calendar')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'calendar' ? 'bg-gray-100' : 'hover:bg-gray-50'
                  }`}
                >
                  <Calendar className="w-4 h-4" />
                </button>
              </div>
              <button
                onClick={() => setShowNewPostModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-sm ml-auto sm:ml-0"
              >
                <Plus className="w-5 h-5" />
                <span className="hidden sm:inline">Novo Post</span>
                <span className="sm:hidden">Novo</span>
              </button>
            </>
          )}
        </div>
      </div>

      {viewMode === 'calendar' ? (
        <CalendarView posts={filteredPosts} />
      ) : (
        <>
          {/* Posts Grid */}
          <div className="grid grid-cols-1 gap-4">
            {filteredPosts.map((post) => (
              <div
                key={post.id}
                onClick={() => setSelectedPost(post)}
                className="bg-white rounded-xl border hover:border-blue-200 hover:shadow-md transition-all cursor-pointer transform hover:scale-[1.01] active:scale-[0.99]"
              >
                <div className="p-4">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className={`flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium ${getChannelColor(post.channel)}`}>
                      {getChannelIcon(post.channel)}
                      <span className="hidden sm:inline">{post.channel}</span>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(post.status)}`}>
                      {post.status}
                    </span>
                  </div>

                  {/* Content */}
                  <h3 className="font-medium text-gray-900 mb-2 line-clamp-1">{post.title}</h3>
                  <p className="text-gray-500 text-sm line-clamp-3">{post.content}</p>

                  {/* Footer */}
                  <div className="mt-4 pt-4 border-t flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span className="hidden sm:inline">
                        {post.scheduled_date ? (
                          new Date(post.scheduled_date).toLocaleDateString()
                        ) : (
                          'Não agendado'
                        )}
                      </span>
                      <span className="sm:hidden">
                        {post.scheduled_date ? (
                          new Date(post.scheduled_date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
                        ) : (
                          'N/A'
                        )}
                      </span>
                    </div>
                    {post.hashtags?.length > 0 && (
                      <div className="flex items-center gap-1">
                        <Hash className="w-4 h-4" />
                        {post.hashtags.length}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredPosts.length === 0 && (
            <div className="text-center py-12 bg-white rounded-xl border">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                {showCompleted ? (
                  <CheckCircle className="w-8 h-8 text-blue-600" />
                ) : (
                  <Plus className="w-8 h-8 text-blue-600" />
                )}
              </div>
              <h3 className="text-gray-900 font-medium mb-2">
                {showCompleted ? 'Nenhum post concluído' : 'Nenhum post em andamento'}
              </h3>
              <p className="text-gray-500 mb-4">
                {showCompleted 
                  ? 'Os posts concluídos aparecerão aqui'
                  : 'Comece criando seu primeiro post'
                }
              </p>
              {!showCompleted && (
                <button
                  onClick={() => setShowNewPostModal(true)}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  Criar primeiro post
                </button>
              )}
            </div>
          )}
        </>
      )}

      {/* Edit Modal */}
      {selectedPost && (
        <PostModal
          post={selectedPost}
          onClose={() => setSelectedPost(null)}
          onSave={(updates) => {
            onUpdatePost(selectedPost.id, updates);
            setSelectedPost(null);
          }}
          onDelete={() => {
            onDeletePost(selectedPost.id);
            setSelectedPost(null);
          }}
        />
      )}

      {/* New Post Modal */}
      <NewPostModal
        isOpen={showNewPostModal}
        onClose={() => setShowNewPostModal(false)}
        onSave={onAddPost}
      />
    </div>
  );
}